import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Plus, Trash2, Send, Check, X } from "lucide-react";
import { toast } from "sonner";

export default function BirimBasvuru() {
  const { user } = useAuth();
  const isSuperAdmin = user.role === "founder" || user.role === "admin";
  const [me, setMe] = useState(null);
  const [units, setUnits] = useState([]);

  useEffect(() => {
    api.get("/memur/me").then(r => setMe(r.data));
    api.get("/memur/units").then(r => setUnits(r.data));
  }, []);

  const myOfficer = me?.officer;
  const isReviewerAnywhere = isSuperAdmin || myOfficer?.is_ps || myOfficer?.is_yps ||
    myOfficer?.units?.some(u => u.position === "chief" || u.position === "deputy_chief");
  const chiefUnitIds = isSuperAdmin || myOfficer?.is_ps || myOfficer?.is_yps
    ? units.map(u => u.id)
    : (myOfficer?.units || []).filter(u => u.position === "chief").map(u => u.unit_id);

  if (!me) return <div className="p-10 text-zinc-500 font-mono text-sm">YÜKLENİYOR...</div>;

  return (
    <div className="p-6 md:p-10 space-y-6 max-w-5xl">
      <header>
        <div className="text-[11px] font-mono text-rose-500 tracking-widest mb-2">// POLİS DEPARTMANI</div>
        <h1 className="font-heading text-4xl font-bold tracking-tight">Birim Başvurusu</h1>
        <p className="text-zinc-500 mt-2">Birimlere başvur, gelen başvuruları incele, birim soruları hazırla.</p>
      </header>

      <Tabs defaultValue="basvur">
        <TabsList className="bg-zinc-950 border border-zinc-900">
          <TabsTrigger value="basvur" data-testid="tab-basvur">Başvur</TabsTrigger>
          <TabsTrigger value="basvurularim" data-testid="tab-basvurularim">Başvurularım</TabsTrigger>
          {isReviewerAnywhere && <TabsTrigger value="gelen" data-testid="tab-gelen">Gelen Başvurular</TabsTrigger>}
          {chiefUnitIds.length > 0 && <TabsTrigger value="sorular" data-testid="tab-sorular">Soru Yönetimi</TabsTrigger>}
        </TabsList>
        <TabsContent value="basvur" className="mt-4"><BasvurSection /></TabsContent>
        <TabsContent value="basvurularim" className="mt-4"><BasvurularimSection myOfficerId={myOfficer?.id} /></TabsContent>
        {isReviewerAnywhere && <TabsContent value="gelen" className="mt-4"><GelenBasvurularSection myOfficerId={myOfficer?.id} /></TabsContent>}
        {chiefUnitIds.length > 0 && <TabsContent value="sorular" className="mt-4"><SorularSection units={units} chiefUnitIds={chiefUnitIds} isSuperAdmin={isSuperAdmin} isTopOfficer={myOfficer?.is_ps || myOfficer?.is_yps} /></TabsContent>}
      </Tabs>
    </div>
  );
}

// ============ BAŞVUR ============
function BasvurSection() {
  const [applicable, setApplicable] = useState([]);
  const [selected, setSelected] = useState(null);
  const [answers, setAnswers] = useState({});

  const load = () => api.get("/memur/birim-basvuru-uygun-birimler").then(r => setApplicable(r.data));
  useEffect(() => { load(); }, []);

  const selectUnit = (unitId) => {
    const u = applicable.find(a => a.unit_id === unitId);
    setSelected(u);
    setAnswers({});
  };

  const submit = async () => {
    const requiredAnswers = selected.questions.map(q => ({
      question_id: q.id, question_text: q.text, answer: answers[q.id] || "",
    }));
    if (requiredAnswers.some(a => !a.answer.trim())) return toast.error("Tüm soruları yanıtlamalısınız");
    try {
      await api.post("/memur/birim-basvurulari", { unit_id: selected.unit_id, answers: requiredAnswers });
      toast.success("Başvurunuz gönderildi");
      setSelected(null); setAnswers({});
    } catch (e) { toast.error(e.response?.data?.detail || "Gönderilemedi"); }
  };

  if (applicable.length === 0) {
    return <Card className="bg-zinc-950 border-zinc-900 p-10 text-center text-zinc-500 text-sm">Şu anda başvuruya açık bir birim yok.</Card>;
  }

  return (
    <div className="space-y-4 max-w-2xl">
      <Select value={selected?.unit_id || ""} onValueChange={selectUnit}>
        <SelectTrigger data-testid="basvur-unit-select" className="bg-zinc-900 border-zinc-800"><SelectValue placeholder="Başvurmak istediğiniz birimi seçin" /></SelectTrigger>
        <SelectContent className="bg-zinc-950 border-zinc-800">
          {applicable.map(u => <SelectItem key={u.unit_id} value={u.unit_id}>{u.unit_name}</SelectItem>)}
        </SelectContent>
      </Select>

      {selected && (
        <Card className="bg-zinc-950 border-zinc-900 p-5 space-y-4">
          {selected.questions.map(q => (
            <div key={q.id}>
              <Label className="text-xs font-mono">{q.text}</Label>
              <Textarea rows={3} value={answers[q.id] || ""} onChange={e => setAnswers({ ...answers, [q.id]: e.target.value })}
                data-testid={`answer-input-${q.id}`} className="bg-zinc-900 border-zinc-800 mt-1.5" />
            </div>
          ))}
          <Button onClick={submit} data-testid="basvuru-submit-button" className="bg-rose-600 hover:bg-rose-700"><Send className="w-4 h-4 mr-2" />Başvuruyu Gönder</Button>
        </Card>
      )}
    </div>
  );
}

// ============ BAŞVURULARIM ============
function BasvurularimSection({ myOfficerId }) {
  const [items, setItems] = useState([]);
  useEffect(() => { api.get("/memur/birim-basvurulari").then(r => setItems(r.data.filter(b => b.officer_id === myOfficerId))); }, [myOfficerId]);

  return (
    <div className="space-y-3">
      {items.length === 0 && <Card className="bg-zinc-950 border-zinc-900 p-10 text-center text-zinc-500 text-sm">Henüz bir başvurunuz yok.</Card>}
      {items.map(b => (
        <Card key={b.id} className="bg-zinc-950 border-zinc-900 p-5" data-testid={`my-basvuru-${b.id}`}>
          <div className="flex items-center gap-2 mb-2">
            <span className={`text-[10px] font-mono px-2 py-0.5 border ${b.status === "answered" ? (b.decision === "accepted" ? "bg-emerald-600/20 text-emerald-400 border-emerald-600/40" : "bg-rose-600/20 text-rose-400 border-rose-600/40") : "bg-amber-600/20 text-amber-400 border-amber-600/40"}`}>
              {b.status === "answered" ? (b.decision === "accepted" ? "Kabul Edildi" : b.decision === "rejected" ? "Reddedildi" : "Yanıtlandı") : "Beklemede"}
            </span>
            <span className="text-[10px] font-mono text-zinc-500">{new Date(b.created_at).toLocaleString("tr-TR")}</span>
          </div>
          {b.response_text && (
            <div className="text-xs text-zinc-400 bg-zinc-900/50 p-3 border-l-2 border-zinc-700">
              <span className="font-mono text-zinc-500">{b.responded_by_name}:</span> {b.response_text}
            </div>
          )}
        </Card>
      ))}
    </div>
  );
}

// ============ GELEN BAŞVURULAR (İncele) ============
function GelenBasvurularSection({ myOfficerId }) {
  const [items, setItems] = useState([]);
  const [reviewOpen, setReviewOpen] = useState(null);
  const [decision, setDecision] = useState("accepted");
  const [responseText, setResponseText] = useState("");

  const load = () => api.get("/memur/birim-basvurulari", { params: { status: "pending" } }).then(r => setItems(r.data.filter(b => b.officer_id !== myOfficerId)));
  useEffect(() => { load(); }, [myOfficerId]);

  const respond = async () => {
    if (!responseText.trim()) return toast.error("Yanıt metni gerekli");
    try {
      await api.post(`/memur/birim-basvurulari/${reviewOpen.id}/respond`, { decision, response_text: responseText });
      toast.success("Başvuru yanıtlandı");
      setReviewOpen(null); setResponseText(""); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Başarısız"); }
  };

  return (
    <div className="space-y-3">
      {items.length === 0 && <Card className="bg-zinc-950 border-zinc-900 p-10 text-center text-zinc-500 text-sm">Bekleyen başvuru yok.</Card>}
      {items.map(b => (
        <Card key={b.id} className="bg-zinc-950 border-zinc-900 p-5" data-testid={`gelen-basvuru-${b.id}`}>
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div className="flex-1 min-w-0">
              <div className="font-semibold mb-2">{b.officer_name}</div>
              <div className="space-y-2 text-sm">
                {b.answers.map((a, i) => (
                  <div key={i}><div className="text-zinc-500 text-xs font-mono">{a.question_text}</div><div className="text-zinc-300">{a.answer}</div></div>
                ))}
              </div>
            </div>
            <Button size="sm" onClick={() => { setReviewOpen(b); setResponseText(""); setDecision("accepted"); }} data-testid={`respond-basvuru-${b.id}`} className="bg-rose-600 hover:bg-rose-700">Yanıtla</Button>
          </div>
        </Card>
      ))}

      <Dialog open={!!reviewOpen} onOpenChange={v => !v && setReviewOpen(null)}>
        <DialogContent className="bg-zinc-950 border-zinc-800">
          <DialogHeader><DialogTitle className="font-heading">Başvuruyu Yanıtla</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="flex gap-2">
              <Button type="button" variant={decision === "accepted" ? "default" : "outline"} onClick={() => setDecision("accepted")} data-testid="decision-accept" className={decision === "accepted" ? "bg-emerald-600" : "border-emerald-800 text-emerald-400"}><Check className="w-4 h-4 mr-1" />Kabul</Button>
              <Button type="button" variant={decision === "rejected" ? "default" : "outline"} onClick={() => setDecision("rejected")} data-testid="decision-reject" className={decision === "rejected" ? "bg-rose-600" : "border-rose-800 text-rose-400"}><X className="w-4 h-4 mr-1" />Red</Button>
            </div>
            <Textarea rows={4} value={responseText} onChange={e => setResponseText(e.target.value)} placeholder="Yanıt metni..." data-testid="response-text-input" className="bg-zinc-900 border-zinc-800" />
          </div>
          <DialogFooter><Button onClick={respond} data-testid="respond-submit-button" className="bg-rose-600 hover:bg-rose-700">Gönder</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ============ SORU YÖNETİMİ ============
function SorularSection({ units, chiefUnitIds, isSuperAdmin, isTopOfficer }) {
  const manageableUnits = isSuperAdmin || isTopOfficer ? units : units.filter(u => chiefUnitIds.includes(u.id));
  const [unitId, setUnitId] = useState(manageableUnits[0]?.id || "");
  const [questions, setQuestions] = useState([]);
  const [published, setPublished] = useState(false);
  const [newQuestion, setNewQuestion] = useState("");

  const load = (uid) => api.get(`/memur/birim-basvuru-sorular/${uid}`).then(r => {
    setQuestions(r.data.questions || []); setPublished(r.data.published || false);
  });
  useEffect(() => { if (unitId) load(unitId); }, [unitId]);

  const addQuestion = () => {
    if (!newQuestion.trim()) return;
    setQuestions([...questions, { id: `tmp-${Date.now()}`, text: newQuestion }]);
    setNewQuestion("");
  };
  const removeQuestion = (id) => setQuestions(questions.filter(q => q.id !== id));

  const save = async () => {
    try {
      await api.put(`/memur/birim-basvuru-sorular/${unitId}`, {
        questions: questions.map(q => ({ text: q.text })),
        published,
      });
      toast.success("Kaydedildi"); load(unitId);
    } catch (e) { toast.error(e.response?.data?.detail || "Başarısız"); }
  };

  return (
    <Card className="bg-zinc-950 border-zinc-900 p-6 space-y-4 max-w-2xl">
      <Select value={unitId} onValueChange={setUnitId}>
        <SelectTrigger data-testid="sorular-unit-select" className="bg-zinc-900 border-zinc-800"><SelectValue /></SelectTrigger>
        <SelectContent className="bg-zinc-950 border-zinc-800">
          {manageableUnits.map(u => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}
        </SelectContent>
      </Select>

      <div className="space-y-2">
        {questions.map((q, i) => (
          <div key={q.id} className="flex items-center gap-2">
            <Input value={q.text} onChange={e => setQuestions(questions.map((qq, ii) => ii === i ? { ...qq, text: e.target.value } : qq))}
              data-testid={`question-input-${i}`} className="bg-zinc-900 border-zinc-800" />
            <Button size="sm" variant="ghost" onClick={() => removeQuestion(q.id)} data-testid={`question-remove-${i}`} className="text-rose-400"><Trash2 className="w-4 h-4" /></Button>
          </div>
        ))}
      </div>
      <div className="flex gap-2">
        <Input value={newQuestion} onChange={e => setNewQuestion(e.target.value)} placeholder="Yeni soru ekle..." data-testid="new-question-input" className="bg-zinc-900 border-zinc-800" />
        <Button onClick={addQuestion} data-testid="add-question-button"><Plus className="w-4 h-4" /></Button>
      </div>

      <label className="flex items-center gap-2 text-sm cursor-pointer">
        <input type="checkbox" checked={published} onChange={e => setPublished(e.target.checked)} data-testid="published-checkbox" className="accent-rose-600" />
        Bu form yayında (başvuru kabul edilsin)
      </label>

      <Button onClick={save} data-testid="save-questions-button" className="bg-rose-600 hover:bg-rose-700">Kaydet</Button>
    </Card>
  );
}
